import { Pressable, View, Platform, ViewStyle, StyleSheet } from "react-native"
import { Feather } from '@expo/vector-icons';
import React, { useEffect, useState, useMemo } from 'react';
import parseByDelimiter from "@utils/parseByDelimiter"
import HorizontalCarousel from "@components/Global/Carousel/HorizontalCarousel"
import { LinearGradient } from "expo-linear-gradient"
import reorder from './utils/reOrder';
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import handleImage from "@utils/handleImage";
import moment from "moment";
import { Separator, SizableText, XStack, YStack } from "tamagui";
import { useDeviceType } from "@hooks/useDeviceType";
import RatingComponent from "@components/RatingComponent";
import getCurrencySymbol from "@hooks/getCurrency";
import { useQueryClient } from "@tanstack/react-query";
import * as Animatable from 'react-native-animatable';
import Button from "@components/UI/Button/Button";

export type DefaultProductProps = {
  item: any;
  dataId: string;
  navigation: any;
  maxWidth?: number;
  innerStyle?: ViewStyle
}

const DefaultProduct = ({ innerStyle, item, dataId, maxWidth, navigation }: DefaultProductProps) => {
  const { data } = item;
  const [initialArray, setInitialArray] = useState([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const queryClient = useQueryClient();
  const theme = themeSelector();
  const [key, setKey] = useState(0);

  const { isTablet } = useDeviceType();
  const tablet = isTablet;

  // Extract the first 3 key-value pairs from item.data
  useEffect(() => {
    if (data) {
      setInitialArray(getFirstThreeKeyValuePairs(data));
    } else {
      setInitialArray([]);
    }
  }, [item, data]);

  const getFirstThreeKeyValuePairs = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    
    try {
      const objLowercase = Object.keys(obj).reduce((acc, key) => {
        if (typeof obj[key] !== 'object') {
          acc[key.toLowerCase()] = obj[key];
        }
        return acc;
      }, {});

      const priorityProperties = ["name", "firstname", "surname", "email", "company", "company name", "companyname"];
      let result = [];
      const exludeProperties = ["_id", "image", "id"];
      
      for (const property of priorityProperties) {
        if (objLowercase.hasOwnProperty(property) && !exludeProperties.includes(property)) {
          result.push([property, objLowercase[property]]);
        }
      }

      if (result.length >= 4) {
        for (let i = 0; i < result.length; i++) {
          if (result[i] && result[i][0] && result[i][0].includes('description')) {
            result = reorder(result, i, result.length - 1);
            break;
          }
        }
        return result;
      }
      
      for (const [key, value] of Object.entries(objLowercase)) {
        if (!result.find(([prop]) => prop === key)) {
          result.push([key, value]);
          if (result.length >= 3) {
            break;
          }
        }
      }
      
      for (let i = 0; i < result.length; i++) {
        if (result[i] && result[i][0] && result[i][0].includes('description')) {
          result = reorder(result, i, result.length - 1);
          break;
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error in getFirstThreeKeyValuePairs:', error);
      return [];
    }
  };

  const parseIfDate = (value) => {
    if (typeof value === 'number') {
      return value.toString();
    }

    if (value) {
      if (value.includes('T') && moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format('DD/MM/YYYY');
      }
    }
    return value;
  };

  // Get product title
  const titleData = initialArray.find(([key, value]) =>
    key.includes('name') || key.includes('title')
  );
  const productTitle = titleData ? titleData[1] : 'Product Name';

  // Get other details (excluding title and image fields)
  const productDetails = initialArray.filter(([key, value]) =>
    !key.includes('image') &&
    !key.includes('name') &&
    !key.includes('title') &&
    !['image', 'id'].includes(key)
  ).slice(0, 3);

  return useMemo(() => (
    <Animatable.View
      useNativeDriver
      animation="fadeIn"
      style={[
        heroStyles.glassCard,
        {
          maxWidth: maxWidth || Platform.select({ web: "100%", default: "95%" }),
          minHeight: 320,
        },
        innerStyle
      ]}
      key={item._id}
      onLayout={({ nativeEvent }) => setDimensions({ width: nativeEvent.layout.width, height: nativeEvent.layout.height })}
    >
      {/* Gradient Background Layer */}
      <LinearGradient
        colors={theme
          ? ['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)']
          : ['rgba(200, 162, 200, 0.25)', 'rgba(135, 206, 235, 0.25)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={heroStyles.gradientBackground}
      />

      {/* Floating Blur Elements */}
      <View style={[heroStyles.floatingBlur, { top: 20, left: 20 }]} />
      <View style={[heroStyles.floatingBlur, { bottom: 20, right: 20 }]} />

      {/* Blur Overlay for Glass Effect */}
      {Platform.OS === 'web' && (
        <View style={heroStyles.blurOverlay} />
      )}

      {/* Content Container */}
      <View style={heroStyles.contentContainer}>
        {/* Image Section */}
        <View style={heroStyles.imageSection}>
          <Pressable
            style={heroStyles.imagePressable}
            onPress={async () => {
              const res = await handleImage(true);
              await apis.addDataImage(dataId, item._id, res).then(res => {
                item.dataImages.push(res.data.data);
                setKey(key + 1);
              }).finally(() => {
                queryClient.invalidateQueries({ queryKey: ['individualData', dataId] });
              })
            }}
          >
            {item.dataImages && item.dataImages?.length > 0 ? (
              <HorizontalCarousel
                imageStyle={heroStyles.carouselImage}
                images={item.dataImages}
                height={180}
                removeSampleMark
              />
            ) : (
              <View style={heroStyles.emptyImageContainer}>
                <LinearGradient
                  colors={theme
                    ? ['rgba(200, 162, 200, 0.1)', 'rgba(135, 206, 235, 0.1)']
                    : ['rgba(200, 162, 200, 0.2)', 'rgba(135, 206, 235, 0.2)']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={heroStyles.emptyImageGradient}
                >
                  <View style={heroStyles.emptyImageIcon}>
                    <Feather
                      name="image"
                      size={32}
                      color={theme ? 'rgba(100, 116, 139, 0.6)' : 'rgba(148, 163, 184, 0.6)'}
                    />
                  </View>
                  <SizableText
                    size="$3"
                    style={[
                      heroStyles.emptyImageText,
                      { color: theme ? 'rgba(100, 116, 139, 0.8)' : 'rgba(148, 163, 184, 0.8)' }
                    ]}
                  >
                    Tap to add image
                  </SizableText>
                </LinearGradient>
              </View>
            )}

            {/* Price Badge */}
            {data?.price && (
              <View style={heroStyles.priceBadge}>
                <LinearGradient
                  colors={['#3b82f6', '#2563eb']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={heroStyles.priceBadgeGradient}
                >
                  <SizableText
                    size="$3"
                    style={heroStyles.priceBadgeText}
                  >
                    {getCurrencySymbol("GBP")}{data?.price || 0}
                  </SizableText>
                </LinearGradient>
              </View>
            )}
          </Pressable>
        </View>

        {/* Content Section */}
        <YStack gap={16} padding={20} flex={1}>
          {/* Title with Gradient Effect */}
          <View>
            <SizableText
              userSelect={"none"}
              numberOfLines={2}
              style={[
                heroStyles.title,
                {
                  color: theme ? '#1e293b' : '#f1f5f9',
                }
              ]}
            >
              {parseIfDate(productTitle)}
            </SizableText>

            {/* Rating and Meta Info */}
            {productDetails.length > 0 && (
              <XStack 
                gap={12}
                alignItems="center"
                marginTop={8}
              >
                <XStack gap={6} alignItems="center">
                  <RatingComponent
                    count={5}
                    defaultValue={4}
                    style={{
                      transform: Platform.select({ web: [{ scale: 0.85 }], default: [{ scale: 0.5 }] }),
                    }}
                    disabled
                  />
                  <SizableText style={[
                    heroStyles.ratingText,
                    { color: theme ? '#64748b' : '#94a3b8' }
                  ]}>
                    (4.0)
                  </SizableText>
                </XStack>
              </XStack>
            )}
          </View>

          {/* Separator */}
          {productDetails.length > 0 && (
            <Separator 
              borderColor={theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'}
              marginVertical={0}
            />
          )}

          {/* Product Details */}
          {productDetails.length > 0 && (
            <YStack gap={12}>
              {productDetails.map(([key, value], index) => (
                <View
                  key={index + 'product_detail'}
                  style={[
                    heroStyles.detailRow,
                    {
                      backgroundColor: theme
                        ? 'rgba(0,0,0,0.02)'
                        : 'rgba(255,255,255,0.05)',
                    }
                  ]}
                >
                  <SizableText
                    style={[
                      heroStyles.detailLabel,
                      { color: theme ? '#64748b' : '#94a3b8' }
                    ]}
                  >
                    {parseByDelimiter(key)}
                  </SizableText>
                  <SizableText
                    numberOfLines={2}
                    style={[
                      heroStyles.detailValue,
                      {
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontStyle: value === "No Data" ? 'italic' : 'normal',
                        opacity: value === "No Data" ? 0.6 : 1
                      }
                    ]}
                  >
                    {parseIfDate(value)}
                  </SizableText>
                </View>
              ))}
            </YStack>
          )}
        </YStack>
      </View>
      
      {/* Open Button */}
      <View style={[heroStyles.buttonContainer]}>
        <Button 
          buttonText={"Open"} 
          textStyle={{ top: 3, right: -10 }} 
          inverse 
          customStyle={[heroStyles.openButton, { paddingTop: 10, top: 10, backgroundColor: 'transparent' }]} 
          iconStyle={{ left: 5 }} 
          icon={'right'} 
          iconColor='white' 
          onPress={() => navigation.navigate('ProductScreen', { dataId: dataId, contentId: item._id })} 
        />
      </View>
    </Animatable.View>
  ), [item, key, dimensions, theme, initialArray, productTitle, productDetails, dataId, navigation]);
}

// Hero-inspired styling
const heroStyles = StyleSheet.create({
  glassCard: {
    borderRadius: 24,
    overflow: 'hidden',
    position: 'relative',
    elevation: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: Platform.OS === 'web' ? 0.15 : 0.2,
    shadowRadius: 24,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    }),
  },
  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  floatingBlur: {
    position: 'absolute',
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: Platform.OS === 'web' ? 'rgba(200, 162, 200, 0.1)' : 'transparent',
    ...(Platform.OS === 'web' && {
      filter: 'blur(40px)',
    }),
    zIndex: 1,
  },
  blurOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Platform.OS === 'web' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    backdropFilter: Platform.OS === 'web' ? 'blur(20px)' : undefined,
    zIndex: 2,
  },
  contentContainer: {
    position: 'relative',
    zIndex: 3,
    backgroundColor: Platform.OS === 'web'
      ? 'rgba(255, 255, 255, 0.95)'
      : 'rgba(255, 255, 255, 0.98)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      marginBottom: 20
    }),
  },
  imageSection: {
    height: 180,
    position: 'relative',
    overflow: 'hidden',
  },
  imagePressable: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  carouselImage: {
    width: "100%",
    height: "100%",
  },
  emptyImageContainer: {
    flex: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyImageGradient: {
    flex: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyImageIcon: {
    width: 64,
    height: 64,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(10px)',
    }),
  },
  emptyImageText: {
    fontWeight: '500',
    textAlign: 'center',
    fontSize: 14,
  },
  priceBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    borderRadius: 16,
    overflow: 'hidden',
    elevation: 4,
    shadowColor: '#3b82f6',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  priceBadgeGradient: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    minWidth: 70,
    alignItems: 'center',
    justifyContent: 'center',
  },
  priceBadgeText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  title: {
    fontSize: Platform.select({ web: 24, default: 20 }),
    fontWeight: '800',
    lineHeight: Platform.select({ web: 32, default: 28 }),
    letterSpacing: -0.5,
  },
  ratingText: {
    fontSize: 12,
    fontWeight: '600',
  },
  detailRow: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 12,
    marginVertical: 2,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  detailValue: {
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20,
  },
  buttonContainer: {
    paddingHorizontal: 20,
    paddingBottom: 5,
    marginTop: -15,
    position: 'relative',
    zIndex: 10,
  },
  openButton: {
    minWidth: "100%",
    width: "60%",
    borderStartStartRadius: 0,
    flex: 1,
    borderTopStartRadius: 0,
    paddingTop: 8,
    justifyContent: "center",
    minHeight: 50,
    borderTopEndRadius: 0,
    borderBottomStartRadius: 30,
    borderBottomEndRadius: 30,
    alignSelf: "center",
  },
});

export default DefaultProduct;